import { catalogClient, type CatalogClient } from './catalogClient'
import {
  isValidCatalogManifest,
  readCatalogCache,
  readManifestCache,
  writeCatalogCache,
  writeManifestCache,
} from './catalogStorage'
import type {
  CatalogBodyGuard,
  CatalogEntryOutcome,
  CatalogManifest,
  CatalogVersion,
  StoredCatalogEntry,
} from './types'

export interface CatalogDeps {
  client?: CatalogClient
  storage?: Storage
}

/**
 * Freshness rules (BE contract `static_catalog_manifest.md`).
 *
 * The manifest is the ONLY entry point; a catalog body is never fetched without it. A cached
 * entry is then admitted by the first of three tiers that accepts it (ORP-20199):
 *
 * 1. `cached.fingerprint === manifest.version` — the whole bundle is unmoved. Steady state:
 *    zero requests, zero writes.
 * 2. Both counters are STRINGS and `cached.version === entry.version` — the bundle moved but
 *    this body did not. The fingerprint is re-stamped (a localStorage write, no network), so
 *    one catalog's bump no longer makes its N siblings pay a conditional GET each.
 * 3. Otherwise — the counter moved, or either side is a number. Revalidate with the stored
 *    ETag: the server compares exact integers and answers 304 (empty body) for anything that
 *    did not really change, so a real edit costs one cheap conditional GET, not a payload.
 *
 * Tier 2 is deliberately string-only. As a JSON *number* the clock-seeded counter is already
 * rounded by `JSON.parse` (ULP 256 — see `CatalogVersion`), so two consecutive bumps are the
 * same double and `===` would report "unchanged" for a catalog that actually moved. The number
 * world therefore falls through to tier 3, where the exact comparison happens server-side.
 *
 * An entry with no cached body is always fetched, even when the fingerprint is unchanged —
 * otherwise a newly adopted catalog would never arrive on a client whose manifest is current.
 *
 * Steady state is therefore one 304 on the manifest and zero body requests per boot.
 */

let generation = 0
let manifestPromise: Promise<CatalogManifest | null> | null = null
const inFlight = new Map<string, Promise<CatalogEntryOutcome<never>>>()

/** Logout / token handoff: drop the memoised manifest and invalidate every late continuation so
 *  it cannot repopulate a cleared cache with the previous session's data. */
export function resetCatalogRuntime(): void {
  generation += 1
  manifestPromise = null
  inFlight.clear()
}

async function readManifest(deps: CatalogDeps): Promise<CatalogManifest | null> {
  const storage = deps.storage ?? localStorage
  const client = deps.client ?? catalogClient
  const own = generation
  const cached = readManifestCache(storage)
  try {
    const result = await client.fetchManifest(cached?.etag)
    if (own !== generation) return null
    if (result.status === 304) return cached?.body ?? null
    if (!isValidCatalogManifest(result.body)) return null
    writeManifestCache({ etag: result.etag, body: result.body }, storage)
    return result.body
  } catch {
    return null
  }
}

/** One manifest read per boot, shared by every catalog consumer. */
export function resolveCatalogManifest(deps: CatalogDeps = {}): Promise<CatalogManifest | null> {
  if (!manifestPromise) {
    const own = generation
    manifestPromise = readManifest(deps).then((manifest) => {
      // A failed read is not memoised for the whole session — the next consumer retries.
      if (!manifest && own === generation) manifestPromise = null
      return manifest
    })
  }
  return manifestPromise
}

function usable<T>(body: T | undefined): CatalogEntryOutcome<T> {
  return body === undefined
    ? { status: 'failed' }
    : { status: 'ready', body, source: 'cache' as const }
}

/** Record that this body is current as of `fingerprint`, keeping the body and its ETag. Also
 *  the self-healing step for a record cached under the pre-ORP-20196 numeric wire: it is
 *  rewritten with the manifest's counter, so the next boot can take the string fast path. */
function restamp<T>(
  name: string,
  cached: StoredCatalogEntry<T>,
  version: CatalogVersion,
  fingerprint: string,
  storage: Storage,
): void {
  writeCatalogCache(
    name,
    {
      etag: cached.etag,
      version,
      fingerprint,
      body: cached.body,
      fetchedAt: cached.fetchedAt,
    },
    storage,
  )
}

/** Tier 2 of the freshness gate: this exact body is already stored. STRING counters only —
 *  a numeric `===` here would read two consecutive bumps as one value and skip forever. */
function bodyIsCurrent(
  cachedVersion: CatalogVersion | null,
  entryVersion: CatalogVersion,
): boolean {
  return (
    typeof cachedVersion === 'string' &&
    typeof entryVersion === 'string' &&
    cachedVersion === entryVersion
  )
}

async function refreshEntry<T>(
  name: string,
  isValidBody: CatalogBodyGuard<T>,
  deps: CatalogDeps,
): Promise<CatalogEntryOutcome<T>> {
  const storage = deps.storage ?? localStorage
  const client = deps.client ?? catalogClient
  const own = generation
  const cached = readCatalogCache(name, isValidBody, storage)

  const manifest = await resolveCatalogManifest(deps)
  if (own !== generation) return { status: 'failed' }
  if (!manifest) return usable(cached?.body)

  const entry = manifest.entries.find((candidate) => candidate.name === name)
  // Registry order is not stable, so entries are always matched by name. An unlisted name means
  // this deployment's mirror does not carry the catalog: the caller owns the legacy source.
  if (!entry) return { status: 'unlisted' }
  if (cached) {
    // Tier 1 — the bundle never moved.
    if (cached.fingerprint === manifest.version) {
      return { status: 'ready', body: cached.body, source: 'cache' }
    }
    // Tier 2 — the bundle moved but this entry did not: re-stamp, never re-request.
    if (bodyIsCurrent(cached.version, entry.version)) {
      restamp(name, cached, entry.version, manifest.version, storage)
      return { status: 'ready', body: cached.body, source: 'cache' }
    }
  }

  try {
    const result = await client.fetchEntry<unknown>(entry.url, cached?.etag)
    if (own !== generation) return { status: 'failed' }
    if (result.status === 304) {
      // Confirmed current: re-stamp the fingerprint so the next boot short-circuits again, and
      // adopt the manifest's counter so that boot can settle at tier 2 instead of tier 3.
      if (!cached) return { status: 'failed' }
      restamp(name, cached, entry.version, manifest.version, storage)
      return { status: 'ready', body: cached.body, source: 'cache' }
    }
    if (!isValidBody(result.body)) return usable(cached?.body)
    writeCatalogCache(
      name,
      {
        etag: result.etag,
        version: entry.version,
        fingerprint: manifest.version,
        body: result.body,
        fetchedAt: new Date().toISOString(),
      },
      storage,
    )
    return { status: 'ready', body: result.body, source: 'network' }
  } catch {
    // A transient mirror failure must not blank a warm catalog. The fingerprint stays unstamped,
    // so the next read revalidates instead of trusting this body forever.
    return usable(cached?.body)
  }
}

/**
 * Resolve one catalog body: cache-first, revalidated against the manifest fingerprint.
 * Concurrent callers for the same `name` share one request.
 */
export function ensureCatalogEntry<T>(
  name: string,
  isValidBody: CatalogBodyGuard<T>,
  deps: CatalogDeps = {},
): Promise<CatalogEntryOutcome<T>> {
  const pending = inFlight.get(name)
  if (pending) return pending as Promise<CatalogEntryOutcome<T>>

  const request = refreshEntry(name, isValidBody, deps).finally(() => {
    if (inFlight.get(name) === (request as Promise<CatalogEntryOutcome<never>>)) {
      inFlight.delete(name)
    }
  })
  inFlight.set(name, request as Promise<CatalogEntryOutcome<never>>)
  return request
}

/** Synchronous warm-cache read for consumers that paint before the manifest decision settles. */
export function readCachedCatalogBody<T>(
  name: string,
  isValidBody: CatalogBodyGuard<T>,
  storage: Storage = localStorage,
): T | null {
  return readCatalogCache(name, isValidBody, storage)?.body ?? null
}
